import { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import { GlobalEntityState, globalStateFromConfig } from "../utils/state";
import { DEFAULT_GLOBAL_STATE } from "../../config/states/entities";
import { VerbChangeStateData } from "../../config/types";
import { LOGS } from "../utils/logging";
import {
	VerbChangeGlobalEntityStateDataSchema,
	VerbChangeGlobalEntityStateTypeSchema,
} from "../../config/schemas";

export interface GlobalEntityStateContextType {
	GlobalEntityState: Record<string, string>;
	setGlobalEntityState: Dispatch<SetStateAction<GlobalEntityState>>;
	updateGlobalEntityState: (data: VerbChangeStateData) => void;
}

const GlobalStateContext = createContext<GlobalEntityStateContextType | undefined>(undefined);

export function useGlobalEntityState() {
	const context = useContext(GlobalStateContext);
	if (!context) {
		throw new Error("useGlobalEntityState must be used within a GlobalEntityStateProvider");
	}
	return context;
}

export function GlobalStateProvider({ children }: { children: ReactNode }) {
	const [GlobalEntityState, setGlobalEntityState] = useState<GlobalEntityState>(
		globalStateFromConfig(Object.values(DEFAULT_GLOBAL_STATE)),
	);

	const updateGlobalEntityState = (data: VerbChangeStateData) => {
		const { stateKey, type, options } = data;
		if (!stateKey || !type || !options || options.length === 0) {
			LOGS.STATE &&
				console.warn(
					"No data provided for state update: expected",
					VerbChangeGlobalEntityStateDataSchema,
					"recieved:",
					data,
				);
			return;
		}
		const getNewValue = (prev: Record<string, string>) => {
			switch (type) {
				case "set":
					return options[0];
				case "toggle": {
					const prevIdx = options.indexOf(prev[stateKey]);
					return options[(prevIdx + 1) % options.length];
				}
				case "random":
					return options[Math.floor(Math.random() * options.length)];
				default:
					LOGS.STATE &&
						console.warn(
							"Invalid type: expected one of",
							VerbChangeGlobalEntityStateTypeSchema.options,
							"but received",
							data.type,
						);
					return undefined;
			}
		};

		setGlobalEntityState((prev) => {
			const newValue = getNewValue(prev);
			if (!newValue) {
				console.error("No value provided for state: ", stateKey);
				return prev;
			}
			return {
				...prev,
				[stateKey]: newValue,
			};
		});
	};

	return (
		<GlobalStateContext.Provider
			value={{
				GlobalEntityState,
				setGlobalEntityState,
				updateGlobalEntityState,
			}}
		>
			{children}
		</GlobalStateContext.Provider>
	);
}
